import { makeAutoObservable } from 'mobx';
import moment from 'moment';
import apiManage from '../../request';
import {
    Mode,
    RevenueReport,
    StatisticsResponse,
    GroupMenuCatalogOptionItem,
    GroupMenuCatalogOptionsResponse,
    MenuSalesByMenuNameResponse,
    MenuSalesMetric,
    emptyMenuSalesByMenuNameResponse,
    normalizeGroupMenuCatalogOptions,
    normalizeMenuCatalogMenusFromApi,
    normalizeMenuSalesByMenuNameResponse,
} from '../../request/api/group-revenue-analysis';
import {
    getPeriodFromDates,
    getDatesForPeriod
} from '../../component/period-date-filter.component';
import { dateValidate } from '../../constants/tool-order';

/** 集团营收分析页状态：时间筛选、三个接口数据、加载与错误 */
class GroupRevenueAnalysisState {
    /** 开始时间，格式 YYYY-MM-DD HH:mm:ss */
    startDate: string = '';

    /** 结束时间，格式 YYYY-MM-DD HH:mm:ss */
    endDate: string = '';

    /** 日期时间选择器当前值（与 startDate/endDate 同步） */
    pickerValue: [Date | null, Date | null] = [null, null];

    /** 集团营业总额仪表盘数据 */
    dashboard: RevenueReport | null = null;

    /** 营收趋势曲线数据 */
    trend: StatisticsResponse | null = null;

    /** 集团菜单销售分析 Tab：menu-catalog-options 归一后的下拉数据 */
    menuCatalogOptions: GroupMenuCatalogOptionsResponse | null = null;

    /**
     * 接口 `res.menus` 归一化后的一级菜单列表（与 `menuCatalogOptions.menus` 同源，单独保存便于后续菜单选择）
     */
    menuCatalogMenus: GroupMenuCatalogOptionItem[] = [];

    /** 集团菜单销售分析 Tab：menu-sales-by-menu-name 图表与排名 */
    menuSalesByMenuName: MenuSalesByMenuNameResponse | null = null;

    /** Tab3 菜单销售分析接口请求中（用于骨架屏） */
    menuSalesLoading = false;

    /** Tab3 下拉合集请求中（不单独占满屏骨架，与数据请求可并行） */
    menuCatalogOptionsLoading = false;

    /** Tab0 集团营业总额：仪表盘+趋势请求中 */
    loadingGroupTotalRevenue = false;

    /** Tab1 多门店对比：仪表盘+趋势请求中 */
    loadingMultiStoreComparison = false;

    /** 错误信息，空表示无错误 */
    errorMessage = '';

    /** 当前选中的 Tab 索引：0 集团营业总额，1 多门店数据对比，2 集团菜单销售分析 */
    activeTabIndex = 0;

    /** 多门店对比选中的门店 restaurant_id（单选，用于下方折线图展示） */
    selectedStoreId = '';

    /** 多门店对比下拉多选的门店 id 列表，为空表示全部 */
    selectedStoreIds: string[] = [];

    /** 多门店对比数据类型：total / dine_in / delivery */
    dataType = 'total';

    /** 菜单销售分析：对比门店多选，为空表示全部门店 */
    dishSalesCompareStoreIds: string[] = [];

    /** 菜单销售分析：菜单筛选（名称，与接口 menu_name 一致；空为不筛选） */
    dishSalesMenuName = '';

    /** 菜单销售分析：菜单类别筛选（名称） */
    dishSalesCategoryName = '';

    /** 菜单销售分析：菜品筛选（名称） */
    dishSalesDishName = '';

    /** 菜单销售分析：折线图指标 金额 / 数量 */
    dishSalesMetric: MenuSalesMetric = MenuSalesMetric.Amount;

    /** 当前选择的集团 string_id，空则使用 responsible_group 第一项 */
    selectedGroupStringId = '';

    /** 取消重复请求：仪表盘 */
    private dashboardAbort: AbortController | null = null;
    private dashboardReqSeq = 0;

    /** 取消重复请求：营收趋势 */
    private trendAbort: AbortController | null = null;
    private trendReqSeq = 0;

    /** 取消重复请求：菜单合集下拉 */
    private menuCatalogAbort: AbortController | null = null;
    private menuCatalogReqSeq = 0;

    /** 取消重复请求：菜单销售分析 */
    private menuSalesAbort: AbortController | null = null;
    private menuSalesReqSeq = 0;

    /** Tab0 / Tab1 批量拉取并发包装，用于正确结束 loading（避免先发起的 Promise.all 误关 loading） */
    private tab0BatchSeq = 0;
    private tab1BatchSeq = 0;

    constructor() {
        makeAutoObservable(this);
        this.initDefaultFilters();
    }

    /**
     * 初始化本页全部筛选条件：默认时间范围为「本月」，其余与字段默认值一致。
     */
    private initDefaultFilters() {
        const [start, end] = getDatesForPeriod('this_month');
        this.startDate = moment(start).format('YYYY-MM-DD HH:mm:ss');
        this.endDate = moment(end).format('YYYY-MM-DD HH:mm:ss');
        this.pickerValue = [start, end];
        this.selectedGroupStringId = '';
        this.activeTabIndex = 0;
        this.selectedStoreId = '';
        this.selectedStoreIds = [];
        this.dataType = 'total';
        this.errorMessage = '';
        this.menuSalesLoading = false;
        this.menuCatalogOptionsLoading = false;
        this.loadingGroupTotalRevenue = false;
        this.loadingMultiStoreComparison = false;
        this.menuCatalogOptions = null;
        this.menuCatalogMenus = [];
        this.menuSalesByMenuName = null;
        this.dishSalesCompareStoreIds = [];
        this.dishSalesMenuName = '';
        this.dishSalesCategoryName = '';
        this.dishSalesDishName = '';
        this.dishSalesMetric = MenuSalesMetric.Amount;
    }

    /** 根据当前 startDate/endDate 推导 API 的 mode */
    getApiMode(): Mode {
        const period = getPeriodFromDates(this.startDate, this.endDate);
        if (period === 'today') return Mode.Today;
        if (period === 'this_week') return Mode.Week;
        if (period === 'this_month') return Mode.Month;
        return Mode.Custom;
    }

    /** 获取请求参数（mode；仅 custom 传 start_time/end_time） */
    getParams(): { mode: Mode; start_time?: string; end_time?: string } {
        const mode = this.getApiMode();
        const params: { mode: Mode; start_time?: string; end_time?: string } = { mode };
        if (mode === Mode.Custom) {
            params.start_time = this.startDate;
            params.end_time = this.endDate;
        }
        return params;
    }

    /** 日期时间范围变更（周期选择或自定义选择器变更时调用） */
    handleDateTime(dates: [Date | null, Date | null]) {
        if (!dates[0] || !dates[1]) {
            this.pickerValue = dates;
            return;
        }
        if (!dateValidate(dates[0], dates[1])) {
            return;
        }
        this.pickerValue = dates;
        this.startDate = moment(dates[0]).format('YYYY-MM-DD HH:mm:ss');
        this.endDate = moment(dates[1]).format('YYYY-MM-DD HH:mm:ss');
    }

    /** 设置仪表盘数据； */
    setDashboard(data: RevenueReport | null) {
        this.dashboard = data;
    }

    /** 设置趋势数据 */
    setTrend(data: StatisticsResponse | null) {
        this.trend = data;
    }

    /** 设置菜单合集下拉（同步一级菜单列表，与 `res.menus` 归一化结果一致） */
    setMenuCatalogOptions(data: GroupMenuCatalogOptionsResponse | null) {
        this.menuCatalogOptions = data;
        this.menuCatalogMenus = data?.menus ?? [];
    }

    /** 设置菜单销售分析数据 */
    setMenuSalesByMenuName(data: MenuSalesByMenuNameResponse | null) {
        this.menuSalesByMenuName = data;
    }

    /** 设置错误信息 */
    setErrorMessage(msg: string) {
        this.errorMessage = msg;
    }

    /** 设置当前 Tab 索引 */
    setActiveTabIndex(index: number) {
        this.activeTabIndex = index;
    }

    /** 设置多门店对比选中的门店（折线图展示用） */
    setSelectedStoreId(id: string) {
        this.selectedStoreId = id;
    }

    /** 设置多门店对比多选的门店列表，为空表示全部 */
    setSelectedStoreIds(ids: string[]) {
        this.selectedStoreIds = ids;
    }

    /** 设置多门店对比数据类型 */
    setDataType(v: string) {
        this.dataType = v;
    }

    /** 设置当前集团（切换集团后需重新 fetch） */
    setSelectedGroupStringId(id: string) {
        this.selectedGroupStringId = id;
    }

    /** 菜单销售分析：对比门店 */
    setDishSalesCompareStoreIds(ids: string[]) {
        this.dishSalesCompareStoreIds = ids;
    }

    /** 菜单销售分析：菜单名称筛选 */
    setDishSalesMenuName(name: string) {
        this.dishSalesMenuName = name;
    }

    /** 菜单销售分析：类别名称筛选 */
    setDishSalesCategoryName(name: string) {
        this.dishSalesCategoryName = name;
    }

    /** 菜单销售分析：菜品名称筛选 */
    setDishSalesDishName(name: string) {
        this.dishSalesDishName = name;
    }

    /** 菜单销售分析：折线图指标 */
    setDishSalesMetric(m: MenuSalesMetric) {
        this.dishSalesMetric = m;
    }

    /** 切换集团时清空多门店对比筛选，避免沿用上一集团的门店 id */
    resetMultiStoreSelection() {
        this.selectedStoreId = '';
        this.selectedStoreIds = [];
    }

    /**
     * 切换集团或离开 Tab3 时重置菜单销售分析筛选与缓存
     */
    resetDishSalesTabFilters() {
        this.menuCatalogOptions = null;
        this.menuCatalogMenus = [];
        this.menuSalesByMenuName = null;
        this.dishSalesCompareStoreIds = [];
        this.dishSalesMenuName = '';
        this.dishSalesCategoryName = '';
        this.dishSalesDishName = '';
        this.dishSalesMetric = MenuSalesMetric.Amount;
    }

    /**
     * 请求集团营收仪表盘
     * @param dataTypeOverride Tab0 传 total；Tab1 不传则用当前 dataType（堂食/外卖等）
     */
    async fetchDashboard(groupId: string, dataTypeOverride?: string) {
        if (!groupId) return;
        this.dashboardAbort?.abort();
        this.dashboardAbort = new AbortController();
        const signal = this.dashboardAbort.signal;
        const seq = ++this.dashboardReqSeq;
        const params = this.getParams();
        const dt = dataTypeOverride ?? this.dataType;
        const extendParams = {
            ...params,
            type: dt === 'total' ? undefined : dt === 'dine_in' ? '1' : '2',
        };
        let res: Awaited<ReturnType<typeof apiManage.get_restaurant_group_revenue_dashboard>>;
        try {
            res = await apiManage.get_restaurant_group_revenue_dashboard(groupId, extendParams, { signal });
        } catch {
            if (signal.aborted) return;
            this.setErrorMessage('');
            this.setDashboard(null);
            return;
        }
        if (signal.aborted || seq !== this.dashboardReqSeq) return;
        if (res && typeof res === 'object' && 'error' in res && (res as { error?: boolean }).error) {
            const msg = String((res as { message?: string }).message ?? '');
            if (/cancel/i.test(msg)) return;
            this.setErrorMessage((res as { message?: string }).message ?? '');
            this.setDashboard(null);
            return;
        }
        this.setErrorMessage('');
        this.setDashboard((res as unknown as RevenueReport) ?? null);
    }

    /** 请求营收趋势曲线 */
    async fetchTrend(groupId: string) {
        if (!groupId) return;
        this.trendAbort?.abort();
        this.trendAbort = new AbortController();
        const signal = this.trendAbort.signal;
        const seq = ++this.trendReqSeq;
        const params = this.getParams();
        let res: Awaited<ReturnType<typeof apiManage.get_group_revenue_trend>>;
        try {
            res = await apiManage.get_group_revenue_trend(groupId, params, { signal });
        } catch {
            if (signal.aborted) return;
            this.setTrend(null);
            return;
        }
        if (signal.aborted || seq !== this.trendReqSeq) return;
        if (res && typeof res === 'object' && 'error' in res && (res as { error?: boolean }).error) {
            const msg = String((res as { message?: string }).message ?? '');
            if (/cancel/i.test(msg)) return;
            this.setTrend(null);
            return;
        }
        this.setTrend(res ?? null);
    }

    /** Tab0：总额仪表盘 + 趋势（与 Tab1 数据类型隔离） */
    async fetchDashboardTrendForTab0(groupId: string) {
        if (!groupId) return;
        const batchId = ++this.tab0BatchSeq;
        this.loadingGroupTotalRevenue = true;
        this.setErrorMessage('');
        try {
            await Promise.all([this.fetchDashboard(groupId, 'total'), this.fetchTrend(groupId)]);
        } catch {
            /* 取消或并发中断：由新批次接管 loading */
        } finally {
            if (batchId === this.tab0BatchSeq) {
                this.loadingGroupTotalRevenue = false;
            }
        }
    }

    /** Tab1：请求当前数据类型对应的仪表盘 */
    async fetchDashboardTrendForTab1(groupId: string) {
        if (!groupId) return;
        const batchId = ++this.tab1BatchSeq;
        this.loadingMultiStoreComparison = true;
        this.setErrorMessage('');
        try {
            await this.fetchDashboard(groupId);
        } catch {
            /* 取消或并发中断：由新批次接管 loading */
        } finally {
            if (batchId === this.tab1BatchSeq) {
                this.loadingMultiStoreComparison = false;
            }
        }
    }

    /**
     * 拉取集团内门店菜单合集（下拉选项）
     */
    async fetchMenuCatalogOptions(groupId: string) {
        if (!groupId) return;
        this.menuCatalogAbort?.abort();
        this.menuCatalogAbort = new AbortController();
        const signal = this.menuCatalogAbort.signal;
        const seq = ++this.menuCatalogReqSeq;
        this.menuCatalogOptionsLoading = true;
        try {
            const restaurant_ids =
                this.dishSalesCompareStoreIds.length > 0
                    ? this.dishSalesCompareStoreIds.join(',')
                    : undefined;
            let res: Awaited<ReturnType<typeof apiManage.get_group_menu_catalog_options>>;
            try {
                res = await apiManage.get_group_menu_catalog_options(groupId, { restaurant_ids }, { signal });
            } catch {
                if (signal.aborted) return;
                this.setMenuCatalogOptions(normalizeGroupMenuCatalogOptions(null));
                return;
            }
            if (signal.aborted || seq !== this.menuCatalogReqSeq) return;
            if (res && typeof res === 'object' && 'error' in res && (res as { error?: boolean }).error) {
                const msg = String((res as { message?: string }).message ?? '');
                if (/cancel/i.test(msg)) return;
                this.setMenuCatalogOptions(normalizeGroupMenuCatalogOptions(null));
                return;
            }
            /** 显式保存接口 `res.menus`，供一级菜单下拉与后续联动 */
            this.menuCatalogMenus = normalizeMenuCatalogMenusFromApi(res);
            this.setMenuCatalogOptions(normalizeGroupMenuCatalogOptions(res));
        } finally {
            if (seq === this.menuCatalogReqSeq) {
                this.menuCatalogOptionsLoading = false;
            }
        }
    }

    /**
     * 拉取集团菜单销售分析（趋势 + 排名）
     * 接口要求必传 menu_name；未选择菜单时不请求并清空展示数据。
     */
    async fetchMenuSalesByMenuNameIfNeeded(groupId: string) {
        if (!groupId) return;
        const menuName = this.dishSalesMenuName.trim();
        if (!menuName) {
            this.menuSalesAbort?.abort();
            this.menuSalesReqSeq += 1;
            this.setMenuSalesByMenuName(emptyMenuSalesByMenuNameResponse());
            this.menuSalesLoading = false;
            return;
        }
        this.menuSalesAbort?.abort();
        this.menuSalesAbort = new AbortController();
        const signal = this.menuSalesAbort.signal;
        const seq = ++this.menuSalesReqSeq;
        this.menuSalesLoading = true;
        try {
            const base = this.getParams();
            const restaurant_ids =
                this.dishSalesCompareStoreIds.length > 0
                    ? this.dishSalesCompareStoreIds.join(',')
                    : undefined;
            const params = {
                ...base,
                restaurant_ids,
                menu_name: menuName,
                category_name: this.dishSalesCategoryName.trim() || undefined,
                dish_name: this.dishSalesDishName.trim() || undefined,
            };
            let res: Awaited<ReturnType<typeof apiManage.get_group_menu_sales_by_menu_name>>;
            try {
                res = await apiManage.get_group_menu_sales_by_menu_name(groupId, params, { signal });
            } catch {
                if (signal.aborted) return;
                this.setMenuSalesByMenuName(emptyMenuSalesByMenuNameResponse());
                return;
            }
            if (signal.aborted || seq !== this.menuSalesReqSeq) return;
            if (res && typeof res === 'object' && 'error' in res && (res as { error?: boolean }).error) {
                const msg = String((res as { message?: string }).message ?? '');
                if (/cancel/i.test(msg)) return;
                this.setMenuSalesByMenuName(emptyMenuSalesByMenuNameResponse());
                return;
            }
            this.setMenuSalesByMenuName(normalizeMenuSalesByMenuNameResponse(res));
        } finally {
            if (seq === this.menuSalesReqSeq) {
                this.menuSalesLoading = false;
            }
        }
    }
}

const groupRevenueAnalysisState = new GroupRevenueAnalysisState();
export default groupRevenueAnalysisState;
